import React, { useEffect, useState } from 'react';
import styles from './CreditsPanel.module.css';
import {
  fetchCredits,
  refusalMessage,
  saveAutoReload,
  startCreditCheckout,
  type AutoReload,
  type CreditOverview,
} from '../../../api/creditsApi';

export interface CreditsPanelProps {
  /** How the browser goes to the Stripe page. Tests replace it; the page uses the real navigation. */
  redirect?: (url: string) => void;
  /** Set by the Billing page when Stripe sent the customer back. */
  returned?: 'success' | 'cancelled' | null;
}

const goTo = (url: string) => {
  window.location.href = url;
};

const usd = (n: number) => `$${n.toLocaleString('en-US')}`;
const count = (n: number) => n.toLocaleString('en-US');

/** The amount the customer typed or picked, or null when it is not a whole number of dollars. */
function parseAmount(custom: string, preset: number | null): number | null {
  if (custom.trim() !== '') {
    const n = Number(custom);
    return Number.isInteger(n) ? n : null;
  }
  return preset;
}

function amountProblem(amount: number | null, overview: CreditOverview): string | null {
  if (amount === null) return 'Enter a whole number of dollars.';
  if (amount < overview.min_recharge_usd) return `The minimum purchase is ${usd(overview.min_recharge_usd)}.`;
  if (overview.presets_usd.includes(amount) || amount >= overview.custom_min_usd) return null;
  return `A custom amount must be at least ${usd(overview.custom_min_usd)}.`;
}

export const CreditsPanel: React.FC<CreditsPanelProps> = ({ redirect = goTo, returned = null }) => {
  const [overview, setOverview] = useState<CreditOverview | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [buyOpen, setBuyOpen] = useState(false);
  const [preset, setPreset] = useState<number | null>(null);
  const [custom, setCustom] = useState('');
  const [customOpen, setCustomOpen] = useState(false);
  const [buying, setBuying] = useState(false);
  const [buyError, setBuyError] = useState<string | null>(null);

  const [auto, setAuto] = useState<AutoReload | null>(null);
  const [autoSaving, setAutoSaving] = useState(false);
  const [autoError, setAutoError] = useState<string | null>(null);
  const [autoSaved, setAutoSaved] = useState(false);

  const load = () =>
    fetchCredits()
      .then((o) => {
        setOverview(o);
        setAuto(o.auto_reload);
        setLoadError(null);
      })
      .catch((e) => setLoadError(refusalMessage(e, 'Your credit balance could not be loaded.')));

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loadError) {
    return (
      <section className={styles.panel} aria-label="AI credits">
        <p className={styles.notice + ' ' + styles.noticeWarn}>{loadError}</p>
      </section>
    );
  }
  if (!overview || !auto) {
    return (
      <section className={styles.panel} aria-label="AI credits">
        <p className={styles.muted}>Loading your credits…</p>
      </section>
    );
  }

  const rate = overview.rate_credits_per_usd;
  const bal = overview.balance;
  const amount = parseAmount(custom, preset);
  const problem = amountProblem(amount, overview);
  const creditsForAmount = amount !== null && problem === null ? amount * rate : null;

  const openBuy = () => {
    setBuyError(null);
    setPreset(overview.presets_usd[0] ?? null);
    setCustom('');
    setCustomOpen(false);
    setBuyOpen(true);
  };

  const buy = async () => {
    if (amount === null || problem) return;
    setBuying(true);
    setBuyError(null);
    try {
      const started = await startCreditCheckout(amount);
      redirect(started.url);
    } catch (e) {
      setBuyError(refusalMessage(e, 'We could not start the payment. Nothing was charged.'));
      setBuying(false);
    }
  };

  const saveAuto = async () => {
    setAutoSaving(true);
    setAutoError(null);
    setAutoSaved(false);
    try {
      const saved = await saveAutoReload({
        enabled: auto.enabled,
        threshold_usd: auto.threshold_usd ?? undefined,
        reload_usd: auto.reload_usd ?? undefined,
      });
      setAuto(saved);
      setOverview({ ...overview, auto_reload: saved });
      setAutoSaved(true);
    } catch (e) {
      setAutoError(refusalMessage(e, 'Automatic top-up could not be saved.'));
    } finally {
      setAutoSaving(false);
    }
  };

  return (
    <div className={styles.stack}>
    <section className={styles.panel} aria-label="AI credits">
      <div className={styles.head}>
        <div>
          <h2 className={styles.title}>AI credits</h2>
          <div className={styles.muted}>
            {usd(1)} buys {count(rate)} credits. Credits pay for AI queries, agent runs, assessments and reports.
          </div>
        </div>
        <button type="button" className={styles.primary} onClick={openBuy}>Buy credits</button>
      </div>

      {returned === 'success' && (
        <p className={styles.notice + ' ' + styles.noticeOk} role="status">
          Thank you. Your credits appear here once Stripe confirms the payment, usually within a minute.
        </p>
      )}
      {returned === 'cancelled' && <p className={styles.muted} role="status">Purchase cancelled. Nothing was charged.</p>}

      <div>
        {bal.unlimited || bal.allowance === null ? (
          <>
            <div className={styles.muted}>Purchased credits available</div>
            <div className={styles.balance} data-testid="credits-balance">{count(bal.purchased_remaining)}</div>
            <div className={styles.muted}>≈ {usd(Math.floor((bal.purchased_remaining / rate) * 100) / 100)} of credit</div>
          </>
        ) : (
          <>
            <div className={styles.muted}>Credits available</div>
            <div className={styles.balance} data-testid="credits-balance">
              {count((bal.allowance_remaining ?? 0) + bal.purchased_remaining)}
            </div>
            <div className={styles.muted}>
              Plan allowance: {count(bal.allowance_remaining ?? 0)} of {count(bal.allowance)} left this month
              {bal.resets_at && <> · resets {new Date(bal.resets_at).toLocaleDateString('en-GB')}</>}
            </div>
            <div className={styles.muted}>
              Purchased: {count(bal.purchased_remaining)} (≈ {usd(Math.floor((bal.purchased_remaining / rate) * 100) / 100)})
            </div>
          </>
        )}
      </div>

    </section>

    <section className={styles.panel} aria-label="Automatic top-up">
        <h2 className={styles.title}>Automatic top-up</h2>
        {auto.failed_at && (
          <p className={styles.notice + ' ' + styles.noticeWarn} role="alert">
            Automatic top-up stopped because your card was declined. Fix the card, then turn it on again.
          </p>
        )}
        {!auto.has_card && (
          <p className={styles.muted}>Buy credits once to save a card. Automatic top-up charges that card.</p>
        )}
        <label className={styles.row}>
          <input
            type="checkbox"
            role="switch"
            aria-label="Turn on automatic top-up"
            checked={auto.enabled}
            disabled={!auto.has_card}
            onChange={(e) => setAuto({ ...auto, enabled: e.target.checked })}
          />
          Turn on automatic top-up
        </label>
        <div className={styles.row}>
          <label className={styles.field}>
            When the balance drops below ($)
            <input
              className={styles.input}
              type="number"
              min={1}
              step={1}
              aria-label="Threshold in dollars"
              value={auto.threshold_usd ?? ''}
              onChange={(e) => setAuto({ ...auto, threshold_usd: e.target.value === '' ? null : Number(e.target.value) })}
            />
          </label>
          <label className={styles.field}>
            Add ($)
            <select
              className={styles.select}
              aria-label="Reload amount in dollars"
              value={auto.reload_usd ?? ''}
              onChange={(e) => setAuto({ ...auto, reload_usd: e.target.value === '' ? null : Number(e.target.value) })}
            >
              <option value="">Choose</option>
              {overview.presets_usd.map((p) => (
                <option key={p} value={p}>{usd(p)} ({count(p * rate)} credits)</option>
              ))}
            </select>
          </label>
        </div>
        <div className={styles.row}>
          <button type="button" className={styles.secondary} onClick={saveAuto} disabled={autoSaving}>
            {autoSaving ? 'Saving…' : 'Save automatic top-up'}
          </button>
          {autoSaved && <span className={styles.muted} role="status">Saved.</span>}
          {autoError && <span className={styles.notice + ' ' + styles.noticeWarn} role="alert">{autoError}</span>}
        </div>
    </section>

    <section className={styles.panel} aria-label="Purchases">
        <h2 className={styles.title}>Purchases</h2>
        {overview.purchases.length === 0 ? (
          <p className={styles.muted}>No purchases yet.</p>
        ) : (
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr><th>Date</th><th>Type</th><th>Amount</th><th>Credits</th><th>Status</th></tr>
              </thead>
              <tbody>
                {overview.purchases.map((p) => (
                  <tr key={p.id}>
                    <td>{p.created_at ? new Date(p.created_at).toLocaleDateString('en-GB') : '—'}</td>
                    <td>{p.kind === 'auto' ? 'Automatic top-up' : 'Purchase'}</td>
                    <td>{usd(p.amount_usd)}</td>
                    <td>{count(p.credits)}</td>
                    <td>{p.status === 'paid' ? 'Paid' : p.status === 'failed' ? 'Failed' : 'Pending'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
    </section>

      {buyOpen && (
        <div className={styles.scrim} role="presentation" onClick={() => !buying && setBuyOpen(false)}>
          <div
            className={styles.dialog}
            role="dialog"
            aria-modal="true"
            aria-labelledby="buy-credits-title"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 id="buy-credits-title" className={styles.title}>Buy AI credits</h2>
            <div className={styles.presets} role="group" aria-label="Preset amounts">
              {overview.presets_usd.map((p) => (
                <button
                  key={p}
                  type="button"
                  className={`${styles.preset} ${preset === p && !customOpen ? styles.presetOn : ''}`}
                  aria-pressed={preset === p && !customOpen}
                  onClick={() => { setPreset(p); setCustom(''); setCustomOpen(false); }}
                >
                  <span className={styles.presetAmount}>{usd(p)}</span>
                  <span className={styles.muted}>{count(p * rate)} credits</span>
                </button>
              ))}
              <button
                type="button"
                className={`${styles.preset} ${customOpen ? styles.presetOn : ''}`}
                aria-pressed={customOpen}
                onClick={() => { setCustomOpen(true); setPreset(null); }}
              >
                <span className={styles.presetAmount}>Custom</span>
                <span className={styles.muted}>Any amount from {usd(overview.custom_min_usd)}</span>
              </button>
            </div>
            {customOpen && (
            <label className={styles.field}>
              Custom amount (minimum {usd(overview.custom_min_usd)})
              <input
                className={styles.input}
                type="number"
                min={overview.custom_min_usd}
                step={1}
                inputMode="numeric"
                aria-label="Custom amount in dollars"
                value={custom}
                onChange={(e) => { setCustom(e.target.value); setPreset(null); }}
              />
            </label>
            )}
            {custom !== '' && problem && <p className={styles.notice + ' ' + styles.noticeWarn} role="alert">{problem}</p>}
            <div className={styles.summary}>
              <div>
                You get <strong>{creditsForAmount !== null ? count(creditsForAmount) : '—'}</strong> credits
              </div>
              <div>
                Total <strong>{amount !== null && problem === null ? usd(amount) : '—'}</strong>
                <span className={styles.muted}> · tax, if any, is shown at checkout</span>
              </div>
            </div>
            {buyError && <p className={styles.notice + ' ' + styles.noticeWarn} role="alert">{buyError}</p>}
            <div className={styles.row}>
              <button type="button" className={styles.primary} onClick={buy} disabled={buying || problem !== null || amount === null}>
                {buying ? 'Opening payment…' : `Continue to payment${amount !== null && problem === null ? ` · ${usd(amount)}` : ''}`}
              </button>
              <button type="button" className={styles.secondary} onClick={() => setBuyOpen(false)} disabled={buying}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreditsPanel;
